import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Users, FileText, Target, MessageSquare, TrendingUp, 
  Activity, Database, AlertCircle, CheckCircle, Clock, Award,
  Brain, Sparkles, Mail, Server, Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/stats`, {
        withCredentials: true
      });
      setStats(response.data);
      
      // Fetch recent activity
      const activityResponse = await axios.get(`${BACKEND_URL}/api/admin/recent-activity`, {
        withCredentials: true
      });
      setRecentActivity(activityResponse.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 403) {
        alert('Admin access required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                className="gap-2"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Admin Dashboard
                </h1>
                <p className="text-sm text-slate-600">System Monitoring & Observability</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => navigate('/feedback-dashboard')}
                className="gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                Feedback
              </Button>
              <Button 
                onClick={fetchAdminData}
                className="gap-2 bg-purple-600 hover:bg-purple-700"
              >
                <Activity className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 max-w-7xl">
        {/* System Health */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-green-600" />
            <h2 className="text-xl font-bold text-slate-900">System Health</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Backend Status</p>
                    <p className="text-2xl font-bold text-green-600">Online</p>
                  </div>
                  <Server className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Database</p>
                    <p className="text-2xl font-bold text-blue-600">Healthy</p>
                  </div>
                  <Database className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">AI Service</p>
                    <p className="text-2xl font-bold text-purple-600">Active</p>
                  </div>
                  <Brain className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Email Service</p>
                    <p className="text-2xl font-bold text-orange-600">Pending</p>
                  </div>
                  <Mail className="w-8 h-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-bold text-slate-900">Key Metrics</h2>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Users className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-slate-900">{stats?.users_total || 0}</p>
                  <p className="text-sm text-slate-600">Total Users</p>
                  <Badge variant="outline" className="mt-2 text-xs">
                    +{stats?.users_today || 0} today
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-slate-900">{stats?.sessions_total || 0}</p>
                  <p className="text-sm text-slate-600">Think Lab Sessions</p>
                  <Badge variant="outline" className="mt-2 text-xs">
                    +{stats?.sessions_today || 0} today
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Target className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-slate-900">{stats?.challenges_total || 0}</p>
                  <p className="text-sm text-slate-600">Challenges</p>
                  <Badge variant="outline" className="mt-2 text-xs">
                    {stats?.challenges_active || 0} active
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Sparkles className="w-8 h-8 text-pink-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-slate-900">{stats?.impact_labs_total || 0}</p>
                  <p className="text-sm text-slate-600">Impact Labs</p>
                  <Badge variant="outline" className="mt-2 text-xs">
                    {stats?.impact_labs_active || 0} active
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <MessageSquare className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-slate-900">{stats?.feedback_total || 0}</p>
                  <p className="text-sm text-slate-600">Feedback</p>
                  <Badge variant="outline" className="mt-2 text-xs">
                    {stats?.feedback_unread || 0} new
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Award className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-slate-900">{stats?.teams_total || 0}</p>
                  <p className="text-sm text-slate-600">Teams</p>
                  <Badge variant="outline" className="mt-2 text-xs">
                    {stats?.teams_active || 0} active
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-bold text-slate-900">Recent Activity</h2>
          </div>
          <Card>
            <CardContent className="pt-6">
              {recentActivity.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No recent activity</p>
              ) : (
                <div className="space-y-4">
                  {recentActivity.slice(0, 10).map((activity, index) => (
                    <div key={index} className="flex items-start gap-3 pb-4 border-b last:border-b-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                        {activity.type === 'user' && <Users className="w-5 h-5 text-purple-600" />}
                        {activity.type === 'session' && <FileText className="w-5 h-5 text-blue-600" />}
                        {activity.type === 'challenge' && <Target className="w-5 h-5 text-green-600" />}
                        {activity.type === 'feedback' && <MessageSquare className="w-5 h-5 text-orange-600" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-slate-900 font-medium">{activity.description}</p>
                        <p className="text-xs text-slate-500 mt-1">{activity.timestamp}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-slate-600" />
            <h2 className="text-xl font-bold text-slate-900">Quick Actions</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-6 flex-col gap-2"
              onClick={() => navigate('/feedback-dashboard')}
            >
              <MessageSquare className="w-6 h-6" />
              <span>View Feedback</span>
            </Button>

            <Button 
              variant="outline" 
              className="h-auto py-6 flex-col gap-2"
              onClick={() => navigate('/community')}
            >
              <Users className="w-6 h-6" />
              <span>User Management</span>
            </Button>

            <Button 
              variant="outline" 
              className="h-auto py-6 flex-col gap-2"
              onClick={() => navigate('/challenges')}
            >
              <Target className="w-6 h-6" />
              <span>Manage Challenges</span>
            </Button>

            <Button 
              variant="outline" 
              className="h-auto py-6 flex-col gap-2"
              onClick={() => window.location.reload()}
            >
              <Activity className="w-6 h-6" />
              <span>System Logs</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
